<template>
  <div class="task-analytics">
    <div class="task-analytics--filters">
      <div class="filter-item">
        <label>{{ $t("common.created-at") }}</label>
        <date-picker v-model="dateRange" range :placeholder="$t('common.date')" :lang="currentLanguage" />
      </div>
      <div class="filter-item">
        <label>{{ $t("task.category") }}</label>
        <filter-select :placeholder="$t('task.category')" :multiple="true" :selected="selectedCategories" @filter="onCategorySearch" @changed="onCategoryChanged" />
      </div>
      <div class="filter-item">
        <label>{{ $t("task.assignee") }}</label>
        <filter-select :placeholder="$t('task.assignee')" :multiple="true" :selected="selectedAssignees" @filter="onAssigneeSearch" @changed="onAssigneeChanged" />
      </div>
      <div class="filter-item">
        <label>{{ $t("task.analytics.status") }}</label>
        <filter-select :placeholder="$t('common.status')" :multiple="true" :selected="selectedStatuses" @filter="onStatusSearch" @changed="onStatusChanged" />
      </div>
      <div class="filter-item">
        <label>{{ $t("task.source") }}</label>
        <filter-select
          :placeholder="$t('department.text')"
          :multiple="true"
          :selected="selectedSourceDepartments"
          @filter="onSourceDepartmentSearch"
          @changed="onSourceDepartmentChanged"
        />
      </div>
      <button class="btn btn-sm btn-light reset-btn" @click="resetFilters" v-if="hasActiveFilters"><i class="fa fa-eraser mr-1"></i> {{ $t("common.clear-selection") }}</button>
    </div>

    <content-loading v-if="isAnalyticsLoading" />

    <div v-else>
      <div class="task-analytics--totals">
        <div class="task-analytics--card kpi">
          <span class="kpi-label">{{ $t("task.analytics.total") }}</span>
          <span class="kpi-value">{{ totals.total }}</span>
        </div>
        <div class="task-analytics--card kpi">
          <span class="kpi-label">{{ $t("task.analytics.open") }}</span>
          <span class="kpi-value">{{ totals.open }}</span>
        </div>
        <div class="task-analytics--card kpi">
          <span class="kpi-label">{{ $t("task.analytics.completed") }}</span>
          <span class="kpi-value">{{ totals.completed }}</span>
        </div>
      </div>

      <div class="task-analytics--row">
        <div class="task-analytics--card chart-card">
          <h5>{{ $t("task.analytics.statusDistribution") }}</h5>
          <div class="chart-wrap" v-if="hasStatusData">
            <doughnut-chart :labels="statusLabels" :datas="statusValues" />
          </div>
          <empty v-else
            ><h5>{{ $t("data.no-data") }}</h5></empty
          >
        </div>

        <div class="task-analytics--card chart-card wide">
          <h5>{{ $t("task.analytics.weeklyFlow") }}</h5>
          <div class="chart-scroll" v-if="hasWeeklyData">
            <div class="chart-wrap" :style="{ minWidth: weeklyChartMinWidth + 'px' }">
              <bar-chart :labels="weeklyLabels" :datasets="weeklyDatasets" />
            </div>
          </div>
          <empty v-else
            ><h5>{{ $t("data.no-data") }}</h5></empty
          >
        </div>
      </div>

      <div class="task-analytics--row">
        <div class="task-analytics--card chart-card wide">
          <h5>{{ $t("task.analytics.cumulative") }}</h5>
          <div class="chart-scroll" v-if="hasWeeklyData">
            <div class="chart-wrap" :style="{ minWidth: weeklyChartMinWidth + 'px' }">
              <line-chart :labels="weeklyLabels" :datasets="cumulativeDatasets" />
            </div>
          </div>
          <empty v-else
            ><h5>{{ $t("data.no-data") }}</h5></empty
          >
        </div>
      </div>

      <div class="task-analytics--row">
        <div class="task-analytics--card chart-card wide">
          <h5>{{ $t("task.analytics.perAssignee") }}</h5>
          <div class="chart-wrap chart-wrap--tall" v-if="hasAssigneeData">
            <bar-chart :labels="assigneeLabels" :datasets="assigneeDatasets" :stacked="true" :height="320" />
          </div>
          <empty v-else
            ><h5>{{ $t("data.no-data") }}</h5></empty
          >
        </div>
      </div>
    </div>

    <div class="task-analytics--card task-list-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">{{ $t("task.analytics.taskList") }}</h5>
        <i v-if="isTasksLoading" class="bx bx-loader bx-spin text-muted" />
      </div>

      <empty v-if="!isTasksLoading && !tasks.length"
        ><h5 class="text-center">{{ $t("data.no-data") }}</h5></empty
      >

      <content-loading v-else-if="isTasksLoading && !tasks.length" />

      <div v-else class="table-responsive task-list-wrap" :class="{ 'is-refetching': isTasksLoading }">
        <table class="table table-hover task-list-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{{ $t("task.name") }}</th>
              <th>{{ $t("task.assignee") }}</th>
              <th>{{ $t("task.source-generic") }}</th>
              <th>{{ $t("task.analytics.status") }}</th>
              <th>{{ $t("task.duedate") }}</th>
              <th>{{ $t("common.created-at") }}</th>
              <th>{{ $t("task.donedate") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in tasks" :key="'analytics-task-' + task._id" class="hand" @click="openDetail(task)">
              <td>{{ task.no }}</td>
              <td>{{ task.name | translate }}</td>
              <td>
                <task-avatar v-if="task.assignee" :account="task.assignee" size="xs" />
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <task-source :caused="task.caused" />
              </td>
              <td>
                <task-status :status="task.status" />
              </td>
              <td>
                <span v-if="task.duedate">
                  {{ task.duedate | date }}
                  <span class="week-badge">{{ $t("task.analytics.weekShort") }} {{ task.duedate | isoWeek }}</span>
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span v-if="task.createdAt">
                  {{ task.createdAt | date }}
                  <span class="week-badge">{{ $t("task.analytics.weekShort") }} {{ task.createdAt | isoWeek }}</span>
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span v-if="task.donedate">
                  {{ task.donedate | date }}
                  <span class="week-badge">{{ $t("task.analytics.weekShort") }} {{ task.donedate | isoWeek }}</span>
                </span>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="tasksPagination" class="d-flex justify-content-center mt-2">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination v-model="page" :total-rows="tasksPagination.totalDocs" :per-page="tasksPagination.limit" limit="10" size="sm"></b-pagination>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FilterSelect from "@/components/common/form/filter-select.vue";
import DoughnutChart from "@modules/organization/components/doughnut";
import DatePicker from "vue2-datepicker";
import BarChart from "./components/bar-chart.vue";
import LineChart from "./components/line-chart.vue";

const STATUS_OPTIONS = ["UNWORKED", "NOT_FIT", "TODO", "IN_PROGRESS", "APPROVAL", "DONE"];

export default {
  name: "TasksAnalyticsView",
  components: { DoughnutChart, BarChart, LineChart, FilterSelect, DatePicker },
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      dateRange: [],
      selectedCategories: [],
      selectedAssignees: [],
      selectedStatuses: [],
      selectedSourceDepartments: [],
      page: 1,
      limit: 15,
    };
  },
  computed: {
    analytics() {
      return this.$store.getters["task/organizationTaskAnalytics"];
    },
    isAnalyticsLoading() {
      return this.$store.getters["task/isOrganizationTaskAnalyticsLoading"];
    },
    tasksPagination() {
      return this.$store.getters["task/analyticsTasksPagination"];
    },
    tasks() {
      return this.tasksPagination?.docs ?? [];
    },
    isTasksLoading() {
      return this.$store.getters["task/isAnalyticsTasksLoading"];
    },
    organizationMembers() {
      return this.$store.getters["organizationMember/accesableOrganizationMembers"] ?? [];
    },
    totals() {
      return this.analytics?.totals ?? { open: 0, completed: 0, total: 0 };
    },
    statusLabels() {
      return (this.analytics?.statusDistribution ?? []).map((s) => this.$t(`task.status.${s.status.toLowerCase()}`));
    },
    statusValues() {
      return (this.analytics?.statusDistribution ?? []).map((s) => s.count);
    },
    hasStatusData() {
      return this.statusValues.some((v) => v > 0);
    },
    weeklyKeys() {
      const incoming = this.analytics?.weeklyIncoming ?? [];
      const completed = this.analytics?.weeklyCompleted ?? [];
      const set = new Set();
      [...incoming, ...completed].forEach((b) => set.add(`${b.year}-${String(b.week).padStart(2, "0")}`));
      return [...set].sort();
    },
    weeklyLabels() {
      const years = new Set(this.weeklyKeys.map((k) => k.split("-")[0]));
      const multiYear = years.size > 1;
      const prefix = this.$t("task.analytics.weekShort");
      return this.weeklyKeys.map((k) => {
        const [year, week] = k.split("-");
        return multiYear ? `${prefix} ${Number(week)} / ${year}` : `${prefix} ${Number(week)}`;
      });
    },
    weeklyDatasets() {
      const lookup = (bucket) => {
        const map = {};
        (bucket ?? []).forEach((b) => (map[`${b.year}-${String(b.week).padStart(2, "0")}`] = b.count));
        return map;
      };
      const inc = lookup(this.analytics?.weeklyIncoming);
      const done = lookup(this.analytics?.weeklyCompleted);
      return [
        { label: this.$t("task.analytics.incoming"), backgroundColor: "#586aff", data: this.weeklyKeys.map((k) => inc[k] ?? 0) },
        { label: this.$t("task.analytics.completed"), backgroundColor: "#50CD89", data: this.weeklyKeys.map((k) => done[k] ?? 0) },
      ];
    },
    hasWeeklyData() {
      return this.weeklyKeys.length > 0;
    },
    cumulativeDatasets() {
      const lookup = (bucket) => {
        const map = {};
        (bucket ?? []).forEach((b) => (map[`${b.year}-${String(b.week).padStart(2, "0")}`] = b.count));
        return map;
      };
      const inc = lookup(this.analytics?.weeklyIncoming);
      const done = lookup(this.analytics?.weeklyCompleted);
      let cInc = 0;
      let cDone = 0;
      const incData = [];
      const doneData = [];
      const openData = [];
      this.weeklyKeys.forEach((k) => {
        cInc += inc[k] ?? 0;
        cDone += done[k] ?? 0;
        incData.push(cInc);
        doneData.push(cDone);
        openData.push(cInc - cDone);
      });
      return [
        {
          label: this.$t("task.analytics.cumulativeIncoming"),
          borderColor: "#586aff",
          backgroundColor: "rgba(88, 106, 255, 0.1)",
          data: incData,
          fill: false,
          tension: 0.3,
        },
        {
          label: this.$t("task.analytics.cumulativeCompleted"),
          borderColor: "#50CD89",
          backgroundColor: "rgba(80, 205, 137, 0.1)",
          data: doneData,
          fill: false,
          tension: 0.3,
        },
        {
          label: this.$t("task.analytics.cumulativeOpen"),
          borderColor: "#FF7C48",
          backgroundColor: "rgba(255, 124, 72, 0.1)",
          data: openData,
          fill: false,
          tension: 0.3,
        },
      ];
    },
    weeklyChartMinWidth() {
      const PER_BAR = 38;
      return Math.max(600, this.weeklyKeys.length * PER_BAR);
    },
    assigneeBuckets() {
      return (this.analytics?.perAssignee ?? [])
        .filter((b) => b.assigneeId)
        .sort((a, b) => b.openCount + b.doneCount - (a.openCount + a.doneCount))
        .slice(0, 15);
    },
    assigneeLabels() {
      return this.assigneeBuckets.map((b) => b.name ?? "—");
    },
    assigneeDatasets() {
      return [
        { label: this.$t("task.analytics.open"), backgroundColor: "#FF7C48", data: this.assigneeBuckets.map((b) => b.openCount) },
        { label: this.$t("task.analytics.completed"), backgroundColor: "#50CD89", data: this.assigneeBuckets.map((b) => b.doneCount) },
      ];
    },
    hasAssigneeData() {
      return this.assigneeBuckets.length > 0;
    },
    hasActiveFilters() {
      return (
        (this.dateRange && this.dateRange.length === 2 && !this.dateRange.includes(null)) ||
        this.selectedCategories.length > 0 ||
        this.selectedAssignees.length > 0 ||
        this.selectedStatuses.length > 0 ||
        this.selectedSourceDepartments.length > 0
      );
    },
    rangeBoundary() {
      if (!this.dateRange || this.dateRange.length !== 2 || this.dateRange.includes(null)) return null;
      return {
        from: new Date(this.dateRange[0]).setHours(0, 0, 0, 0),
        to: new Date(this.dateRange[1]).setHours(23, 59, 59, 999),
      };
    },
    analyticsFilters() {
      const filters = {};
      if (this.selectedCategories.length) filters.categoryIds = this.selectedCategories.map((c) => c._id);
      if (this.selectedAssignees.length) filters.assigneeIds = this.selectedAssignees.map((a) => a.accountId ?? a._id);
      if (this.selectedStatuses.length) filters.statuses = this.selectedStatuses.map((s) => s._id);
      if (this.selectedSourceDepartments.length) filters.sourceDepartmentIds = this.selectedSourceDepartments.map((d) => d._id);
      if (this.rangeBoundary) filters.dateRange = this.rangeBoundary;
      return filters;
    },
    paginationRequest() {
      const query = {};
      if (this.selectedCategories.length) {
        query.category = { value: { $in: this.selectedCategories.map((c) => c._id) }, isObject: true };
      }
      if (this.selectedAssignees.length) {
        query.assignee = { value: { $in: this.selectedAssignees.map((a) => a.accountId ?? a._id) }, isObject: true };
      }
      if (this.selectedStatuses.length) {
        query.status = { value: { $in: this.selectedStatuses.map((s) => s._id) }, isObject: true };
      }
      if (this.selectedSourceDepartments.length) {
        query["caused.entity.department"] = { value: { $in: this.selectedSourceDepartments.map((d) => d._id) }, isObject: true };
      }
      if (this.rangeBoundary) {
        query.createdAt = { value: { $gte: this.rangeBoundary.from, $lte: this.rangeBoundary.to }, isObject: true };
      }
      return {
        options: { page: this.page, limit: this.limit, sort: { priority: "desc", no: "asc" } },
        query,
      };
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val) this.reload();
      },
    },
    analyticsFilters: {
      deep: true,
      handler() {
        if (!this.active) return;
        this.page = 1;
        this.reload();
      },
    },
    page() {
      if (this.active) this.loadTasks();
    },
  },
  methods: {
    formatDate(d) {
      try {
        return new Date(d).toLocaleDateString(this.currentLanguage);
      } catch (e) {
        return "";
      }
    },
    async reload() {
      this.loadAnalytics();
      this.loadTasks();
    },
    async loadAnalytics() {
      try {
        await this.$store.dispatch("task/fetchOrganizationTaskAnalytics", this.analyticsFilters);
      } catch (e) {
        this.$handleHttpErrorByStatusCode?.(e);
      }
    },
    async loadTasks() {
      try {
        await this.$store.dispatch("task/fetchAnalyticsTasks", this.paginationRequest);
      } catch (e) {
        this.$handleHttpErrorByStatusCode?.(e);
      }
    },
    openDetail(task) {
      this.$store.commit("task/showTaskDetailModal", true);
      this.$store.dispatch("task/fetchDetail", task._id);
    },
    resetFilters() {
      this.dateRange = [];
      this.selectedCategories = [];
      this.selectedAssignees = [];
      this.selectedStatuses = [];
      this.selectedSourceDepartments = [];
      this.page = 1;
    },
    async onSourceDepartmentSearch(value, callback) {
      try {
        const response = await this.$store.dispatch("organizationDepartment/paginate", {
          options: { limit: 200 },
          query: value ? { name: { value } } : {},
        });
        callback(response.data?.docs ?? []);
      } catch (e) {
        callback([]);
      }
    },
    onSourceDepartmentChanged(values) {
      this.selectedSourceDepartments = Array.isArray(values) ? values : values ? [values] : [];
    },
    async onCategorySearch(value, callback) {
      try {
        const response = await this.$store.dispatch("taskCategory/paginate", {
          options: { limit: 200 },
          query: value ? { name: { value } } : {},
        });
        callback(response.data?.docs ?? []);
      } catch (e) {
        callback([]);
      }
    },
    onCategoryChanged(values) {
      this.selectedCategories = Array.isArray(values) ? values : values ? [values] : [];
    },
    onAssigneeSearch(value, callback) {
      const v = (value || "").toLowerCase();
      const all = this.organizationMembers;
      const filtered = v ? all.filter((m) => (m.name || "").toLowerCase().includes(v)) : all;
      callback(filtered);
    },
    onAssigneeChanged(values) {
      this.selectedAssignees = Array.isArray(values) ? values : values ? [values] : [];
    },
    onStatusSearch(value, callback) {
      const v = (value || "").toLowerCase();
      const options = STATUS_OPTIONS.map((s) => ({ _id: s, name: this.$t(`task.status.${s.toLowerCase()}`) }));
      const filtered = v ? options.filter((o) => o.name.toLowerCase().includes(v)) : options;
      callback(filtered);
    },
    onStatusChanged(values) {
      this.selectedStatuses = Array.isArray(values) ? values : values ? [values] : [];
    },
  },
};
</script>

<style lang="scss" scoped>
.task-analytics {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &--filters {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 0.75rem;
    padding: 1rem;
    align-items: flex-end;

    .filter-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 16rem;
      flex: 1 1 16rem;

      label {
        margin: 0;
        color: #6b7280;
        font-size: 0.8125rem;
        font-weight: 500;
      }
    }

    .reset-btn {
      height: 2.25rem;
      align-self: flex-end;
    }
  }

  &--totals {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }

  &--row {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }

  &--card {
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 0.75rem;
    padding: 1.25rem;
    flex: 1 1 280px;

    &.kpi {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .kpi-label {
        color: #6b7280;
        font-size: 0.875rem;
      }
      .kpi-value {
        color: #071437;
        font-size: 1.75rem;
        font-weight: 700;
      }
    }

    &.chart-card {
      min-width: 320px;

      h5 {
        margin-bottom: 1rem;
        color: #071437;
        font-weight: 600;
      }

      &.wide {
        flex: 2 1 480px;
      }
    }

    &.task-list-card {
      h5 {
        margin-bottom: 1rem;
        color: #071437;
        font-weight: 600;
      }
    }
  }

  .chart-wrap {
    height: 280px;
    position: relative;

    &--tall {
      height: 340px;
    }
  }

  .chart-scroll {
    overflow-x: auto;
    overflow-y: hidden;
  }

  .task-list-wrap {
    transition: opacity 0.15s ease;

    &.is-refetching {
      opacity: 0.55;
      pointer-events: none;
    }
  }

  .task-list-table {
    font-size: 0.875rem;

    thead th {
      color: #6b7280;
      font-weight: 600;
      border-bottom: 1px solid #e8eaed;
    }

    tbody tr {
      cursor: pointer;
      &:hover {
        background: #f9fafb;
      }
    }
  }

  .week-badge {
    display: inline-block;
    margin-left: 0.375rem;
    padding: 0.0625rem 0.4rem;
    border-radius: 0.25rem;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 0.6875rem;
    font-weight: 600;
    white-space: nowrap;
  }
}
</style>
